import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import Dashboard from "./components/Dashboard";
import { AuthProvider, useAuth } from "./api/AuthContext";
import { LoadingProvider } from "./api/LoadingContext";
import Sidebar from "./components/Sidebar";
import BayarCashForm from "./components/BayarCashForm";
import ChandaReceipt from "./components/ChandaReceipt";
import Receipt from "./components/Receipt";
import EmailSentPage from "./pages/EmailSentPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import ResetPasswordFirstTimePage from "./pages/ResetPasswordFirstTimePage";
import SpinnerOverlay from "./components/SpinnerOverlay";
import AuthPage from "./pages/AuthPage";
import UserPage from "./pages/UserPage";
import NotFoundPage from "./pages/NotFoundPage";
import Profile from "./components/Profile";
import AdminDashboardPage from "./pages/AdminDashboardPage";
import AdminTransactionDetailsPage from "./pages/AdminTransactionDetailsPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import OtpVerifyPage from "./pages/OtpVerifyPage";
import AdminTransactionPage from "./pages/AdminTransactionPage";
import GuidePage from "./pages/GuidePage";
import UserTransactionPage from "./pages/UserTransactionPage";
import MuqamiDashboardPage from "./pages/MuqamiDashboardPage";
import UserAccessManagementPage from "./pages/UserAccessManagementPage";
import {
  ROLE_ADMIN,
  ROLE_USER_MAAL,
  hasAnyRole,
  hasRole,
} from "./utils/roleUtils";

// Protected Route Wrapper
const ProtectedRoute = ({ children }) => {
  const { token, user, authLoading } = useAuth();

  if (authLoading) return <SpinnerOverlay />;

  if (!token) return <Navigate to="/auth" replace />;
  if (
    user?.isMustResetPassword &&
    window.location.pathname !== "/reset-password-first"
  ) {
    return <Navigate to="/reset-password-first" replace />;
  } else if (
    !user?.isMustResetPassword &&
    window.location.pathname === "/reset-password-first"
  ) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Admin-only Route Wrapper (falls back to 404 for non-admin)
const AdminRoute = ({ children }) => {
  const { user, authLoading } = useAuth();
  if (authLoading) return <SpinnerOverlay />;
  if (!hasRole(user, ROLE_ADMIN)) return <NotFoundPage />;
  return children;
};

const RoleRoute = ({ allowedRoles = [], children }) => {
  const { user, authLoading } = useAuth();
  if (authLoading) return <SpinnerOverlay />;
  if (hasRole(user, ROLE_ADMIN) && !hasAnyRole(user, allowedRoles)) {
    return <Navigate to="/" replace />;
  }
  if (!hasAnyRole(user, allowedRoles)) return <NotFoundPage />;
  return children;
};

const UserRoute = ({ children }) => {
  const { user, authLoading } = useAuth();
  if (authLoading) return <SpinnerOverlay />;
  if (hasRole(user, ROLE_ADMIN)) {
    return <Navigate to="/admin/transactions" replace />;
  }
  return children;
};

const PublicRoute = ({ children }) => {
  const { token } = useAuth();

  return token ? <Navigate to="/" replace /> : children;
};

const RootSwitch = () => {
  const { user } = useAuth();
  if (!user) return <SpinnerOverlay />;
  const isAdmin = hasRole(user, ROLE_ADMIN);
  return isAdmin ? <AdminDashboardPage /> : <Dashboard />;
};

// Layout with Sidebar
const Layout = ({ children }) => {
  return (
    <div className="flex h-screen w-screen flex-col-reverse bg-[#f6f3ea] md:flex-row md:overflow-clip md:p-4">
      <Sidebar />
      <div className="h-full w-full flex-1 overflow-y-scroll md:ml-3">
        <div>{children}</div>
      </div>
    </div>
  );
};

function App() {
  return (
    <LoadingProvider>
      <Router>
        <AuthProvider>
          <Routes>
            <Route
              path="/auth"
              element={
                <PublicRoute>
                  <AuthPage />
                </PublicRoute>
              }
            />

            <Route
              path="/email-sent"
              element={
                <PublicRoute>
                  <EmailSentPage />
                </PublicRoute>
              }
            />

            {/* Protected Routes */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Layout>
                    <RootSwitch />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/pay"
              element={
                <ProtectedRoute>
                  <Layout>
                    <BayarCashForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Profile />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/edit-profile"
              element={
                <ProtectedRoute>
                  <Layout>
                    <UserPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/receipt/:id"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ChandaReceipt />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/transactions"
              element={
                <ProtectedRoute>
                  <UserRoute>
                    <Layout>
                      <UserTransactionPage />
                    </Layout>
                  </UserRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/muqami-dashboard"
              element={
                <ProtectedRoute>
                  <RoleRoute allowedRoles={[ROLE_USER_MAAL]}>
                    <Layout>
                      <MuqamiDashboardPage />
                    </Layout>
                  </RoleRoute>
                </ProtectedRoute>
              }
            />
            <Route path="/receipt" element={<Receipt />} />
            <Route
              path="/reset-password-first"
              element={
                <ProtectedRoute>
                  <ResetPasswordFirstTimePage />
                </ProtectedRoute>
              }
            />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route
              path="/transaction/:id"
              element={
                <ProtectedRoute>
                  <AdminRoute>
                    <Layout>
                      <AdminTransactionDetailsPage />
                    </Layout>
                  </AdminRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/transactions"
              element={
                <ProtectedRoute>
                  <AdminRoute>
                    <Layout>
                      <AdminTransactionPage />
                    </Layout>
                  </AdminRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/user-access"
              element={
                <ProtectedRoute>
                  <AdminRoute>
                    <Layout>
                      <UserAccessManagementPage />
                    </Layout>
                  </AdminRoute>
                </ProtectedRoute>
              }
            />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/verify-otp" element={<OtpVerifyPage />} />
            <Route
              path="/guide"
              element={
                <PublicRoute>
                  <GuidePage />
                </PublicRoute>
              }
            />
            {/* Catch-all route for 404 */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </AuthProvider>
      </Router>
    </LoadingProvider>
  );
}

export default App;
